import React, { useState } from 'react';
import { getDefaultPersona } from '@scale/shared/types/remoAdmin';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';
import BaseSelectProject from './SelectProject';

import { TaskType } from '@scale/shared-types';
import { QMShowProjectResponse } from '@scale/shared/qm/qmRequestTypes';
import { SelectProps } from 'react-scale/components/design-system/Select';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextInput,
} from 'react-scale/components/design-system';
import { SelectOption } from 'corp/components/UI/Select';
import { makeStyles } from 'design-system/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { parseObjectIds } from 'react-scale/corp/lib/parseInputText';
import _ from 'lodash';

interface ISelectProjectProps extends SelectProps {
  placeholder?: string;
  showProjectId?: boolean;
  customer?: any;
  taskType?: TaskType | null;
  disallowedTaskTypes?: any;
  includeTestProjects?: boolean;
  exclusivelySyntheticProjects?: boolean;
  showAllByDefault?: boolean;
  width?: string;
  multi?: boolean;
  remoAdminPersonaId?: string;
  noRemoAdminCtxUpdate?: boolean;
  onChange?: SelectProps['onChange'] | ((v: any) => void);
  ref?: any;
  onManualIdInsert?: (selectedProjects: SelectOption[]) => void;
  closeOnSelect?: boolean;
  onSelectResetsInput?: boolean;
}

const SelectProject: React.FC<ISelectProjectProps> = props => {
  const styles = useStyles();

  const remoAdminContext = useRemoAdminCtx();
  if (remoAdminContext.isRemoAdmin) {
    if (!remoAdminContext.currentPersona) {
      const defaultPersona = !props.remoAdminPersonaId
        ? getDefaultPersona(remoAdminContext.remoAdminPersonas)
        : remoAdminContext.remoAdminPersonas.find(p => p._id);
      if (defaultPersona) remoAdminContext.setCurrentPersona(defaultPersona);
    }
  }

  const remoAdminPersonaId = React.useMemo(
    () => remoAdminContext.currentPersona?._id,
    [remoAdminContext.currentPersona],
  );
  const [showManualIdInsertionDialog, setShowManualIdInsertionDialog] = useState(false);
  const [projectIds, setProjectIds] = useState('');
  const [isInserting, setIsInserting] = useState(false);

  const projectSelectProps = { ...props, remoAdminPersonaId, remoAdminCtx: remoAdminContext };

  return (
    <>
      <div className={styles.selectProjectContainer}>
        <div className={styles.selectProject}>
          <BaseSelectProject {...projectSelectProps} />
        </div>
        {props.onManualIdInsert && props.multi && (
          <div className={styles.manualIdInsertButtonContainer}>
            <Button
              className={styles.manualIdInsertButton}
              onClick={() => {
                setShowManualIdInsertionDialog(true);
              }}
            >
              <FontAwesomeIcon icon="paste" />
            </Button>
          </div>
        )}
      </div>
      <Dialog
        fullWidth
        open={showManualIdInsertionDialog}
        onClose={() => {
          setShowManualIdInsertionDialog(false);
        }}
      >
        <DialogTitle>Manual ID Insertion</DialogTitle>
        <DialogContent>
          <div>Insert a comma-separated list of valid, existent Project IDs here:</div>
          <div>
            <TextInput
              fullWidth
              multiline
              value={projectIds}
              onChange={e => setProjectIds(e.target.value)}
              rows={3}
            />
          </div>
        </DialogContent>
        <DialogActions style={{ justifyContent: 'flex-end' }}>
          <>
            <Button
              className={styles.manualIdInsertDialogButton}
              disabled={isInserting}
              onClick={() => {
                setShowManualIdInsertionDialog(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              className={styles.manualIdInsertDialogButton}
              disabled={isInserting}
              onClick={async () => {
                setIsInserting(true);

                const splitInput = _.uniq(projectIds.split(','));
                const splitInputWithoutSpaceChars = splitInput.map(input =>
                  _.replace(input, /\s/g, ''),
                );
                const splitProjectIds = splitInputWithoutSpaceChars.filter(
                  input => parseObjectIds(input).length === 1,
                );
                const projectsPromises: Promise<QMShowProjectResponse | null>[] = [];
                splitProjectIds.forEach(projectId => {
                  projectsPromises.push(
                    (async () => {
                      try {
                        const project = await apiFetch(null, `corp-api/qm/projects/${projectId}`);
                        return project;
                      } catch {
                        return null;
                      }
                    })(),
                  );
                });
                const projects = _.compact(await Promise.all(projectsPromises));
                const projectIdsWithLabel = projects.map(project => ({
                  label: `${project.name} (${project.id})`,
                  value: project.id,
                }));

                if (!props.noRemoAdminCtxUpdate && !props.multi) {
                  remoAdminContext?.setSelectedProject(projectIdsWithLabel[0]?.value ?? '');
                }

                props.onManualIdInsert?.(projectIdsWithLabel);

                setShowManualIdInsertionDialog(false);
                setIsInserting(false);
              }}
            >
              {isInserting ? <CircularProgress size="10px" color="inherit" /> : 'Confirm'}
            </Button>
          </>
        </DialogActions>
      </Dialog>
    </>
  );
};

const useStyles = makeStyles(() => ({
  selectProjectContainer: {
    display: 'flex',
  },
  selectProject: {
    flex: '1',
  },
  manualIdInsertButtonContainer: {
    width: 'auto',
  },
  manualIdInsertButton: {
    minWidth: '44px',
    height: '36px !important',
    padding: '0px 10px !important',
    marginLeft: '8px',
  },
  manualIdInsertDialogButton: {
    minWidth: '85px',
  },
}));

export default SelectProject;
